import React, { useRef, useState, useEffect } from "react";
import { Eraser, PenLine, Fingerprint } from "lucide-react";
import { Button } from "@/components/ui/button";

export const SignaturePad = ({ value, onChange, testid = "signature-pad", tablet = false, label }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const [hasInk, setHasInk] = useState(!!value);
  const height = tablet ? 220 : 150;

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.lineWidth = tablet ? 3 : 2.2;
    ctx.lineCap = "round";
    ctx.strokeStyle = "#0B2545";
    if (value) {
      const img = new Image();
      img.onload = () => ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      img.src = value;
    }
  }, []); // eslint-disable-line

  const pos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const t = e.touches ? e.touches[0] : e;
    return {
      x: (t.clientX - rect.left) * (canvasRef.current.width / rect.width),
      y: (t.clientY - rect.top) * (canvasRef.current.height / rect.height),
    };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.beginPath();
    ctx.moveTo(x, y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const { x, y } = pos(e);
    const ctx = canvasRef.current.getContext("2d");
    ctx.lineTo(x, y);
    ctx.stroke();
    setHasInk(true);
  };

  const end = () => {
    if (!drawing.current) return;
    drawing.current = false;
    onChange(canvasRef.current.toDataURL("image/png"));
  };

  const clear = () => {
    const canvas = canvasRef.current;
    canvas.getContext("2d").clearRect(0, 0, canvas.width, canvas.height);
    setHasInk(false);
    onChange(null);
  };

  const wrapper = tablet
    ? "rounded-2xl border-2 border-emerald-400 bg-emerald-50/40 p-3 shadow-sm"
    : "";

  return (
    <div className={`space-y-2 ${wrapper}`} data-testid={testid}>
      {tablet && (
        <div className="flex items-center gap-2 text-emerald-700">
          <Fingerprint className="h-5 w-5" />
          <span className="text-sm font-semibold">{label || "Assinatura do colaborador"} — assine com o dedo</span>
        </div>
      )}
      <div className={`relative rounded-xl border-2 border-dashed bg-white ${tablet ? "border-emerald-300" : "border-[#E2E8F0] bg-slate-50/60"}`}>
        <canvas
          ref={canvasRef}
          width={640}
          height={height}
          style={{ height }}
          className="w-full touch-none rounded-xl"
          onMouseDown={start}
          onMouseMove={move}
          onMouseUp={end}
          onMouseLeave={end}
          onTouchStart={start}
          onTouchMove={move}
          onTouchEnd={end}
        />
        {!hasInk && (
          <div className="pointer-events-none absolute inset-0 flex items-center justify-center gap-2 text-slate-400">
            <PenLine className="h-4 w-4" />
            <span className="text-sm">{tablet ? "Toque e arraste o dedo para assinar" : "Assine no campo acima"}</span>
          </div>
        )}
      </div>
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400">Assinatura eletrônica (validação por hash SHA-256)</span>
        <Button type="button" variant="ghost" size={tablet ? "default" : "sm"} onClick={clear} data-testid={`${testid}-clear`}>
          <Eraser className="mr-1 h-4 w-4" /> Limpar
        </Button>
      </div>
    </div>
  );
};
